import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { GraduationCap, BookOpen, ShieldCheck, Library, Globe, ClipboardCheck } from 'lucide-react';
import PasswordInput from '../components/PasswordInput';

export default function Login() {
  const [selectedRole, setSelectedRole] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setError('');
    setUsername('');
    setPassword('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password, selectedRole);

    if (result.success) {
      if (selectedRole === 'student') {
        navigate('/student');
      } else if (selectedRole === 'teacher') {
        navigate('/teacher');
      } else if (selectedRole === 'administrator') {
        navigate('/admin');
      } else if (selectedRole === 'online_administrator') {
        navigate('/online-admin');
      } else if (selectedRole === 'mon_inspector') {
        navigate('/mon');
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleBack = () => {
    setSelectedRole(null);
    setError('');
    setUsername('');
    setPassword('');
  };

  if (!selectedRole) {
    return (
      <div className="min-h-screen flex">
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8" style={{backgroundColor: '#F8EDE2'}}>
          <div className="w-full max-w-md">
            <div className="text-center mb-12">
              <div className="flex items-center justify-center mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_lesson-board-app/artifacts/8fx974k1_logo%20%281%29.png" 
                  alt="Азбукарче Logo" 
                  className="w-40 h-40 object-contain"
                />
              </div>
              <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 mb-3">
                БНУ Азбукарче
              </h1>
              <p className="text-lg text-slate-600">Електронен дневник</p>
            </div>

            <div className="space-y-4">
              <Button
                data-testid="student-login-btn"
                onClick={() => handleRoleSelect('student')}
                className="w-full h-16 text-lg bg-[#00966E] hover:bg-[#007a59] text-white font-medium rounded-md shadow-sm transition-all"
              >
                <GraduationCap className="mr-3 h-6 w-6" />
                Вход Ученик
              </Button>

              <Button
                data-testid="teacher-login-btn"
                onClick={() => handleRoleSelect('teacher')}
                className="w-full h-16 text-lg bg-[#00966E] hover:bg-[#007a59] text-white font-medium rounded-md shadow-sm transition-all"
              >
                <BookOpen className="mr-3 h-6 w-6" />
                Вход Учител
              </Button>

              <Button
                data-testid="library-login-btn"
                asChild
                className="w-full h-16 text-lg bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-md shadow-sm transition-all"
              >
                <Link to="/library">
                  <Library className="mr-3 h-6 w-6" />
                  Вход Библиотека
                </Link>
              </Button>
            </div>

            <div className="mt-8 flex flex-col items-center gap-2">
              <Button
                data-testid="admin-login-btn"
                onClick={() => handleRoleSelect('administrator')}
                variant="link"
                className="text-slate-600 hover:text-[#00966E]"
              >
                <ShieldCheck className="mr-2 h-4 w-4" />
                Администратор
              </Button>
              <Button
                data-testid="online-admin-login-btn"
                onClick={() => handleRoleSelect('online_administrator')}
                variant="link"
                className="text-slate-600 hover:text-[#00966E]"
              >
                <Globe className="mr-2 h-4 w-4" />
                Администратор Онлайн обучение
              </Button>
              <Button
                data-testid="mon-login-btn"
                onClick={() => handleRoleSelect('mon_inspector')}
                variant="link"
                className="text-slate-600 hover:text-[#00966E]"
              >
                <ClipboardCheck className="mr-2 h-4 w-4" />
                МОН Инспектор
              </Button>
            </div>

            <div className="mt-6 text-center">
              <a
                href="https://azbukarche.co.uk"
                className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#00966E] transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Назад към сайта
              </a>
            </div>
          </div>
        </div>

        <div className="hidden lg:block lg:w-1/2 login-bg" />
      </div>
    );
  }

  const roleLabels = {
    student: 'Ученик',
    teacher: 'Учител',
    administrator: 'Администратор',
    online_administrator: 'Администратор Онлайн обучение',
    mon_inspector: 'МОН Инспектор'
  };

  const roleIcons = {
    student: <GraduationCap className="h-8 w-8" />,
    teacher: <BookOpen className="h-8 w-8" />,
    administrator: <ShieldCheck className="h-8 w-8" />,
    online_administrator: <Globe className="h-8 w-8" />,
    mon_inspector: <ClipboardCheck className="h-8 w-8" />
  };

  return (
    <div className="min-h-screen flex">
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8" style={{backgroundColor: '#F8EDE2'}}>
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#00966E] text-white rounded-full mb-4">
              {roleIcons[selectedRole]}
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              {roleLabels[selectedRole]}
            </h1>
            <p className="text-slate-600">Въведете вашите данни за вход</p>
          </div>

          <Card className="p-6 border border-slate-200 shadow-sm">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username">Потребителско име</Label>
                <Input
                  id="username"
                  data-testid="username-input"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="password">Парола</Label>
                <PasswordInput
                  id="password"
                  data-testid="password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1"
                />
              </div>

              {error && (
                <div data-testid="error-message" className="text-sm text-[#D01C1F] bg-red-50 p-3 rounded-md">
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  className="flex-1"
                  data-testid="back-btn"
                >
                  Назад
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-[#00966E] hover:bg-[#007a59] text-white"
                  data-testid="login-submit-btn"
                >
                  {loading ? 'Влизане...' : 'Вход'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/2 login-bg" />
    </div>
  );
}
